import type React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useJournal, useCreateJournalEntry, useUpdateJournalEntry } from '../hooks/useJournal'
import { BackLink } from '../components/BackLink'

export default function JournalForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)

  const { data: entries } = useJournal()
  const createEntry = useCreateJournalEntry()
  const updateEntry = useUpdateJournalEntry()

  const existing = entries?.find((e) => e.id === id)

  const today = new Date().toISOString().slice(0, 10)

  const [entryDate, setEntryDate] = useState(existing?.entry_date ?? today)
  const [title, setTitle] = useState(existing?.title ?? '')
  const [content, setContent] = useState(existing?.content ?? '')
  const [tagsRaw, setTagsRaw] = useState((existing?.tags ?? []).join(', '))
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    const tags = tagsRaw
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)

    const payload = {
      entry_date: entryDate,
      title: title.trim() || null,
      content: content.trim(),
      tags,
    }

    try {
      if (isEdit && id) {
        await updateEntry.mutateAsync({ id, ...payload })
      } else {
        await createEntry.mutateAsync(payload)
      }
      navigate('/journal')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const isPending = isEdit ? updateEntry.isPending : createEntry.isPending

  const commonInputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    boxSizing: 'border-box',
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 680 }}>
      <BackLink fallback="/journal" />
      <h1>{isEdit ? 'Edit entry' : 'New journal entry'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
              Date
            </label>
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              style={commonInputStyle}
            />
          </div>
          <div style={{ flex: 2 }}>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give this entry a name…"
              style={commonInputStyle}
            />
          </div>
        </div>

        <div style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
            Content <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            rows={10}
            placeholder="Write your thoughts, notes, observations…"
            style={{ ...commonInputStyle, resize: 'vertical', lineHeight: 1.6 }}
          />
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
            Tags (optional, comma-separated)
          </label>
          <input
            type="text"
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="e.g. idea, problem, win, filament"
            style={commonInputStyle}
          />
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>
            Separate tags with commas. Tags are lowercased automatically.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={isPending || !content.trim()}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
            }}
          >
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save entry'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
          >
            Cancel
          </button>
        </div>

        {errorMessage && (
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}
